import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    try {
      await fetch('https://readdy.ai/api/form/d6sqjojvrivh3q8km5t0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email }).toString(),
      });
      setEmailSent(true);
      setEmail('');
    } catch {
      setEmailSent(true);
    }
  };

  return (
    <footer className="bg-[#0A1018]">
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-white/10">
          <div className="lg:col-span-1">
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Fighting for homeowners, one claim at a time. Licensed public adjusters maximizing your insurance payout.
            </p>
            <form data-readdy-form onSubmit={handleNewsletterSubmit} className="flex gap-2">
              <input
                name="email"
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#1B78D4] transition-colors"
              />
              <button
                type="submit"
                className="bg-[#1B78D4] hover:bg-[#1560B8] text-white font-bold px-4 py-2.5 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                {emailSent ? <i className="ri-check-line" /> : <i className="ri-send-plane-fill" />}
              </button>
            </form>
            {emailSent && <p className="text-green-400 text-xs mt-2">Thanks! We'll be in touch.</p>}
          </div>

          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">Services</h4>
            <ul className="space-y-3">
              {['Roof Damage Claims', 'Hurricane Claims', 'Water Damage Claims', 'Denied Claims', 'Fire Damage Claims'].map((item) => (
                <li key={item}>
                  <button
                    onClick={() => scrollTo('services')}
                    className="text-gray-400 hover:text-[#5BA8E5] text-sm transition-colors cursor-pointer text-left"
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">Company</h4>
            <ul className="space-y-3">
              {[
                { label: 'How It Works', id: 'how-it-works' },
                { label: 'Why Choose Us', id: 'why-us' },
                { label: 'Testimonials', id: 'trust' },
                { label: 'Book Inspection', id: 'booking' },
              ].map((item) => (
                <li key={item.label}>
                  <button
                    onClick={() => scrollTo(item.id)}
                    className="text-gray-400 hover:text-[#5BA8E5] text-sm transition-colors cursor-pointer"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-phone-fill text-[#5BA8E5] text-base" />
                </div>
                <div>
                  <a href="tel:+18132857094" className="text-white font-bold text-sm hover:text-[#5BA8E5] transition-colors">(813) 285-7094</a>
                  <p className="text-gray-500 text-xs">Mon–Fri, 9am–5pm</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-mail-fill text-[#5BA8E5] text-base" />
                </div>
                <a href="mailto:claims@striveclaims.com" className="text-gray-400 hover:text-[#5BA8E5] text-sm transition-colors">
                  claims@striveclaims.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-map-pin-fill text-[#5BA8E5] text-base" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">13194 US HWY 301 S</p>
                  <p className="text-gray-400 text-sm">Suite 408</p>
                  <p className="text-gray-400 text-sm">Riverview, FL 33578</p>
                </div>
              </li>
            </ul>
            <div className="flex items-center gap-3 mt-6">
              {[
                { icon: 'ri-facebook-fill', label: 'Facebook' },
                { icon: 'ri-instagram-line', label: 'Instagram' },
                { icon: 'ri-linkedin-fill', label: 'LinkedIn' },
                { icon: 'ri-youtube-fill', label: 'YouTube' },
              ].map((social) => (
                <a
                  key={social.label}
                  href="#"
                  aria-label={social.label}
                  className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-[#1B78D4]/25 hover:text-[#5BA8E5] text-gray-400 rounded-lg border border-white/10 hover:border-[#1B78D4]/50 transition-all duration-200"
                >
                  <i className={`${social.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-xs">
            &copy; {new Date().getFullYear()} Strive Claims — Public Adjusting Firm. All rights reserved.
            Licensed Public Adjuster. State License #G200498.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" rel="nofollow" className="text-gray-500 hover:text-gray-300 text-xs transition-colors">Privacy Policy</a>
            <span className="text-gray-600">|</span>
            <a href="#" rel="nofollow" className="text-gray-500 hover:text-gray-300 text-xs transition-colors">Terms of Service</a>
            <span className="text-gray-600">|</span>
            <a href="#" rel="nofollow" className="text-gray-500 hover:text-gray-300 text-xs transition-colors">Disclaimer</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
